import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient, handleAxiosError } from 'lib/api';
import type { MutationConfig } from 'lib/react-query';
import queryKeys from './queryKeys';
import { isAxiosError } from 'axios';
import type { z } from 'zod';
import type { goToTestEntryValidator } from '../validation';

type GoToTestPayload = z.infer<typeof goToTestEntryValidator.schema>;

interface UpdateGoToTestPayload extends GoToTestPayload {
  go_to_test_id: number;
}

interface GoToTestResponse {
  message: string;
}

interface ErrorResponse {
  message: string;
  details?: string;
}

export const addGoToTestFlow = async (
  params: GoToTestPayload,
): Promise<GoToTestResponse> => {
  const res = await apiClient.post<GoToTestResponse>(queryKeys.goToTest, params);

  if (isAxiosError<ErrorResponse>(res)) {
    return handleAxiosError(res, 'Failed to add "Go to test"');
  }
  return res.data;
};

export const updateGoToTest =
  async ({
           go_to_test_id,
           ...params
         }: UpdateGoToTestPayload): Promise<GoToTestResponse> => {
    const res = await apiClient.put<GoToTestResponse>(
      `${queryKeys.goToTest}/${go_to_test_id}`,
      params,
    );

    if (isAxiosError<ErrorResponse>(res)) {
      return handleAxiosError(res, 'Failed to update "Go to test"');
    }
    return res.data;
  };

type MutationFnType = (data: GoToTestPayload) => ReturnType<typeof addGoToTestFlow>;
type UpdateMutationFnType = (data: UpdateGoToTestPayload) => ReturnType<typeof updateGoToTest>;

interface UseAddOptions {
  config?: MutationConfig<MutationFnType>;
}

interface UseUpdateOptions {
  config?: MutationConfig<UpdateMutationFnType>;
}

export const useAddGoToTest = ({ config }: UseAddOptions = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...config,
    onSuccess: async (...args) => {
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.goToTest],
      });

      if (config?.onSuccess) {
        await config.onSuccess(...args);
      }
    },
    onError: (...args) => {
      if (config?.onError) {
        config.onError(...args);
      }
    },
    mutationFn: addGoToTestFlow,
  });
};

export const useUpdateGoToTest = ({ config }: UseUpdateOptions = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...config,
    onSuccess: async (...args) => {
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.goToTest],
      });
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.goToTestDetails],
      });

      if (config?.onSuccess) {
        await config.onSuccess(...args);
      }
    },
    onError: (...args) => {
      if (config?.onError) {
        config.onError(...args);
      }
    },
    mutationFn: updateGoToTest,
  });
};
